// OpenTelemetry browser SDK initialisation — traces, metrics, logs.
//
// Registers three signal providers and exports them over OTLP/HTTP to a
// browser-facing collector. The collector must be CORS-configured for this
// app's origin.
//
// • Traces  — WebTracerProvider + BatchSpanProcessor → OTLPTraceExporter.
//             Auto-instruments document-load, fetch, XHR, user-interaction
//             via getWebAutoInstrumentations. Every user-triggered HTTP call
//             to our own APIs carries the W3C `traceparent` header; backend
//             services join the same trace on receipt.
// • Metrics — MeterProvider + PeriodicExportingMetricReader →
//             OTLPMetricExporter. Core Web Vitals (LCP, INP, CLS, FCP, TTFB)
//             are recorded as histograms named `web_vitals.<name>`. The export
//             interval is configurable via VITE_OTEL_METRIC_EXPORT_INTERVAL_MS
//             and defaults to 60 s.
// • Logs    — LoggerProvider + BatchLogRecordProcessor → OTLPLogExporter.
//             Surface kept minimal; feature code continues to use the project
//             logger (`@core/logging/logger`) — wiring an OTel transport on
//             top of it is a follow-up.
//
// Coexists with Sentry: Sentry handles error/crash reporting; OTel handles
// distributed tracing + metrics + structured logs. Neither stack is modified
// by the other.
//
// Call `initOtel()` from `app/bootstrap/initObservability.ts` AFTER initSentry
// so Sentry's browser-tracing integration is in place before OTel registers
// its own fetch/XHR patches, and BEFORE app.mount so document-load timing
// covers the initial navigation.

import {
  context,
  metrics,
  propagation,
  SpanStatusCode,
  trace,
  type Histogram,
  type Meter,
  type Tracer as OtelApiTracer,
} from '@opentelemetry/api'
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { W3CTraceContextPropagator } from '@opentelemetry/core'
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http'
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web'
import { Resource } from '@opentelemetry/resources'
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs'
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics'
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME } from '@opentelemetry/semantic-conventions/incubating'
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric as WebVitalMetric } from 'web-vitals'

import { config } from '@core/config/config'
import { setTracer } from './tracer'
import type { Span, SpanAttributeValue, Tracer, TraceContext } from './tracerTypes'

// ── OTel-backed Tracer (adapts OTel API → project Tracer interface) ──────────

function wrapSpan(otelSpan: ReturnType<OtelApiTracer['startSpan']>): Span {
  return {
    get name(): string {
      return otelSpan.spanContext().spanId // closest proxy; real name lives inside SDK
    },
    setAttribute(key: string, value: SpanAttributeValue): void {
      otelSpan.setAttribute(key, value)
    },
    setAttributes(attrs: Record<string, SpanAttributeValue>): void {
      otelSpan.setAttributes(attrs)
    },
    addEvent(name: string, attrs?: Record<string, SpanAttributeValue>): void {
      otelSpan.addEvent(name, attrs)
    },
    setStatus(code: 'unset' | 'ok' | 'error', description?: string): void {
      const mapped =
        code === 'ok'
          ? SpanStatusCode.OK
          : code === 'error'
            ? SpanStatusCode.ERROR
            : SpanStatusCode.UNSET
      otelSpan.setStatus({ code: mapped, message: description })
    },
    recordException(error: unknown): void {
      if (error instanceof Error) {
        otelSpan.recordException(error)
      } else {
        otelSpan.recordException(String(error))
      }
    },
    end(): void {
      otelSpan.end()
    },
  }
}

function buildOtelTracer(tracer: OtelApiTracer): Tracer {
  return {
    startSpan(name: string, attrs?: Record<string, SpanAttributeValue>): Span {
      const otelSpan = tracer.startSpan(name, { attributes: attrs })
      return wrapSpan(otelSpan)
    },

    withSpan<T>(
      name: string,
      fn: (span: Span) => T | Promise<T>,
      attrs?: Record<string, SpanAttributeValue>,
    ): T | Promise<T> {
      const otelSpan = tracer.startSpan(name, { attributes: attrs })
      const wrapped = wrapSpan(otelSpan)
      const ctx = trace.setSpan(context.active(), otelSpan)

      const finish = (result: T): T => {
        otelSpan.end()
        return result
      }

      let result: T | Promise<T>
      try {
        result = context.with(ctx, () => fn(wrapped))
      } catch (err) {
        otelSpan.recordException(err instanceof Error ? err : String(err))
        otelSpan.end()
        throw err
      }

      if (result instanceof Promise) {
        return result.then(finish, (err: unknown) => {
          otelSpan.recordException(err instanceof Error ? err : String(err))
          otelSpan.end()
          throw err
        })
      }

      return finish(result)
    },

    getActiveContext(): TraceContext | undefined {
      const active = trace.getActiveSpan()
      if (!active) return undefined
      const ctx = active.spanContext()
      return {
        traceId: ctx.traceId,
        spanId: ctx.spanId,
        traceFlags: ctx.traceFlags,
      }
    },
  }
}

// ── SDK registration ─────────────────────────────────────────────────────────

export const initOtel = (): void => {
  if (!config.otel.enabled) {
    // Either explicitly disabled, or no collector configured — keep the no-op
    // tracer. Safe in dev and CI.
    return
  }

  const baseEndpoint = config.otel.baseEndpoint
  if (!baseEndpoint && !hasPerSignalOverrides()) {
    // Defensive: VITE_OTEL_ENABLED was set true without an endpoint — bail.
    return
  }

  // Only propagate traceparent on requests to our own APIs. Injecting it on
  // third-party domains (Mixpanel, Sumsub, CDNs) is a privacy/security risk
  // and would fail their CORS preflight.
  const ownApiUrls: RegExp[] = [
    new RegExp(`^${escapeRegex(config.authApiUrl)}`),
    new RegExp(`^${escapeRegex(config.investorApiUrl)}`),
  ]

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: config.otel.serviceName,
    [ATTR_SERVICE_VERSION]: config.app.version,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: config.appEnv,
  })

  // ── Traces ────────────────────────────────────────────────────────────────
  const traceExporter = new OTLPTraceExporter({
    url: signalUrl(baseEndpoint, 'traces', config.otel.tracesEndpoint),
  })

  const tracerProvider = new WebTracerProvider({
    resource,
    spanProcessors: [new BatchSpanProcessor(traceExporter)],
  })

  // ZoneContextManager keeps async context (Promises, await) on the same trace.
  tracerProvider.register({
    contextManager: new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  })

  // Also set as the global propagator so manual context extraction works.
  propagation.setGlobalPropagator(new W3CTraceContextPropagator())

  registerInstrumentations({
    tracerProvider,
    instrumentations: [
      // Bundles document-load, fetch, XHR, user-interaction. Per-instrumentation
      // configs override defaults; here we scope traceparent injection to our
      // own APIs only (see ownApiUrls above).
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls: ownApiUrls,
        },
        '@opentelemetry/instrumentation-xml-http-request': {
          propagateTraceHeaderCorsUrls: ownApiUrls,
        },
      }),
    ],
  })

  // Wire the OTel-backed tracer into the project's observable tracer surface
  // so existing `tracer.withSpan(...)` call sites start producing real spans.
  const otelTracer = tracerProvider.getTracer(config.otel.serviceName, config.app.version)
  setTracer(buildOtelTracer(otelTracer))

  // ── Metrics ───────────────────────────────────────────────────────────────
  const metricExporter = new OTLPMetricExporter({
    url: signalUrl(baseEndpoint, 'metrics', config.otel.metricsEndpoint),
  })

  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: metricExporter,
        exportIntervalMillis: config.otel.metricExportIntervalMs,
      }),
    ],
  })

  metrics.setGlobalMeterProvider(meterProvider)

  // Core Web Vitals → OTLP histograms. The single most useful frontend signal
  // and not covered by auto-instrumentations-web. One histogram per vital so
  // dashboards can graph p50/p75/p95 separately per metric.
  registerWebVitalsAsHistograms(meterProvider.getMeter(config.otel.serviceName))

  // ── Logs ──────────────────────────────────────────────────────────────────
  const logExporter = new OTLPLogExporter({
    url: signalUrl(baseEndpoint, 'logs', config.otel.logsEndpoint),
  })

  const loggerProvider = new LoggerProvider({ resource })
  loggerProvider.addLogRecordProcessor(new BatchLogRecordProcessor(logExporter))

  // Surfaces a global logger provider for any future @opentelemetry/api-logs
  // consumer (e.g. when a project-logger OTel transport lands). Feature code
  // continues to call the project logger (`@core/logging/logger`) today.
  // The `logs` API is in @opentelemetry/api-logs — set on the provider only.
  // No global setter exists on the stable `metrics`/`trace` surfaces' style;
  // callers obtain a logger via `loggerProvider.getLogger(name)`.
  void loggerProvider
}

// ── Web Vitals → OTLP histograms ─────────────────────────────────────────────

/**
 * Records each Core Web Vital into a dedicated histogram instrument so the
 * collector / backend can compute p50/p75/p95 over arbitrary windows. Unit
 * follows the `web-vitals` lib: milliseconds for everything except CLS (which
 * is a unit-less layout-shift score).
 */
function registerWebVitalsAsHistograms(meter: Meter): void {
  const histograms: Record<string, Histogram> = {
    LCP: meter.createHistogram('web_vitals.lcp', {
      description: 'Largest Contentful Paint',
      unit: 'ms',
    }),
    INP: meter.createHistogram('web_vitals.inp', {
      description: 'Interaction to Next Paint',
      unit: 'ms',
    }),
    CLS: meter.createHistogram('web_vitals.cls', {
      description: 'Cumulative Layout Shift (unit-less score)',
      unit: '1',
    }),
    FCP: meter.createHistogram('web_vitals.fcp', {
      description: 'First Contentful Paint',
      unit: 'ms',
    }),
    TTFB: meter.createHistogram('web_vitals.ttfb', {
      description: 'Time To First Byte',
      unit: 'ms',
    }),
  }

  const record = (metric: WebVitalMetric): void => {
    const histogram = histograms[metric.name]
    if (!histogram) return
    histogram.record(metric.value, {
      'web_vitals.rating': metric.rating,
      'web_vitals.navigation_type': metric.navigationType,
    })
  }

  onLCP(record)
  onINP(record)
  onCLS(record)
  onFCP(record)
  onTTFB(record)
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Escape a string for safe use as a regex literal. */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Resolve the URL for one OTLP signal. Per-signal override wins; otherwise
 * suffix the shared base with the standard `/v1/<signal>` path. A trailing
 * slash on the base is tolerated.
 */
function signalUrl(base: string, signal: 'traces' | 'metrics' | 'logs', override: string): string {
  if (override) return override
  const trimmed = base.endsWith('/') ? base.slice(0, -1) : base
  return `${trimmed}/v1/${signal}`
}

function hasPerSignalOverrides(): boolean {
  return (
    config.otel.tracesEndpoint.length > 0 ||
    config.otel.metricsEndpoint.length > 0 ||
    config.otel.logsEndpoint.length > 0
  )
}
